import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { checkAvailability, createBooking, getRooms, getUser } from '../../services/api';
import '../../styles/BookingPage.css';

const getLocalDateValue = (date = new Date()) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const BookingForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Get room IDs from URL params - memoize to prevent infinite loops
  const roomIdsParam = searchParams.get('rooms');
  const roomIds = useMemo(() => {
    return roomIdsParam ? roomIdsParam.split(',').map(id => parseInt(id)).filter(id => !isNaN(id)) : [];
  }, [roomIdsParam]);
  
  // Get booking type from URL params
  const bookingType = searchParams.get('type') || 'regular';
  const isCampBooking = bookingType === 'camp';
  const isTimeFirstBooking = searchParams.get('source') === 'time';
  const initialDate = searchParams.get('date') || '';
  const initialStartTime = searchParams.get('start') || '';
  const initialEndTime = searchParams.get('end') || '';
  
  // State management
  const [selectedDate, setSelectedDate] = useState(initialDate);
  const [selectedEndDate, setSelectedEndDate] = useState(''); // For camp bookings
  const [availableHours, setAvailableHours] = useState([]);
  const [unavailableSlots, setUnavailableSlots] = useState([]);
  const [startTime, setStartTime] = useState(initialStartTime);
  const [endTime, setEndTime] = useState(initialEndTime);
  const [selectedRoomDetails, setSelectedRoomDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [conflictDetails, setConflictDetails] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);
  const isAdmin = user?.role === 'admin';
  const supportsMultiDay = isCampBooking || isAdmin;

  useEffect(() => {
    getUser().then(response => setUser(response.data)).catch(() => {});
  }, []);

  // Get today's date in YYYY-MM-DD format for min date
  const today = getLocalDateValue();

  // Memoize fetchAvailability to prevent recreating on every render
  const fetchAvailability = useCallback(async () => {
    if (!selectedDate || roomIds.length === 0) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await checkAvailability(selectedDate, roomIds);
      setAvailableHours(response.data.available_hours || []);
      setUnavailableSlots(isAdmin ? [] : response.data.unavailable_slots || []);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to fetch availability');
      setAvailableHours([]);
      setUnavailableSlots([]);
    } finally {
      setLoading(false);
    }
  }, [selectedDate, roomIds, isAdmin]);

  // Fetch availability when date is selected (only for regular bookings)
  useEffect(() => {
    if (!isCampBooking && selectedDate && roomIds.length > 0) {
      fetchAvailability();
    } else {
      setAvailableHours([]);
      setUnavailableSlots([]);
    }
  }, [selectedDate, roomIds, fetchAvailability, isCampBooking]);

  useEffect(() => {
    const fetchSelectedRooms = async () => {
      if (roomIds.length === 0) {
        setSelectedRoomDetails([]);
        return;
      }

      try {
        const response = await getRooms();
        setSelectedRoomDetails(
          response.data.filter((room) => roomIds.includes(room.id)),
        );
      } catch (err) {
        console.error('Failed to load selected room details:', err);
        setSelectedRoomDetails([]);
      }
    };

    fetchSelectedRooms();
  }, [roomIds]);

  // Format hour
  const formatHour = (hour) => {
    if (hour === 24) {
      return '11:59 PM';
    }
    if (hour === 0) {
      return '12:00 AM';
    }
    const period = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour > 12 ? hour - 12 : hour;
    return `${displayHour}:00 ${period}`;
  };

  // Format time from string (for unavailable slots)
  const formatTimeString = (timeStr) => {
    // If it's already formatted, return as is
    if (timeStr.includes('AM') || timeStr.includes('PM')) {
      return timeStr;
    }
    return timeStr;
  };

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
    if (supportsMultiDay) {
      // For camp bookings, set end date to same as start date initially
      setSelectedEndDate(e.target.value);
    }
    setStartTime('');
    setEndTime('');
    setConflictDetails(null);
  };

  const handleEndDateChange = (e) => {
    setSelectedEndDate(e.target.value);
    setConflictDetails(null);
  };

  const handleStartTimeChange = (e) => {
    setStartTime(e.target.value);
    // Reset end time if it's before start time
    if (endTime && parseInt(e.target.value) >= parseInt(endTime)) {
      setEndTime('');
    }
  };

  const handleEndTimeChange = (e) => {
    setEndTime(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is authenticated before submitting
    let currentUser;
    try {
      currentUser = (await getUser()).data;
    } catch (err) {
      // User is not authenticated, redirect to login
      alert('Please log in to complete your booking.');
      navigate('/login', { state: { from: '/booking-form', search: window.location.search } });
      return;
    }
    
    if (supportsMultiDay) {
      if (!selectedDate || !selectedEndDate || !startTime || !endTime) {
        setError('Please select start date, end date, start time, and end time');
        return;
      }
      
      // For camp bookings, validate dates
      const startDateObj = new Date(selectedDate);
      const endDateObj = new Date(selectedEndDate);
      
      if (endDateObj < startDateObj) {
        setError('End date must be on or after start date');
        return;
      }
      
      // If same day, validate times
      if (selectedDate === selectedEndDate && parseInt(startTime) >= parseInt(endTime)) {
        setError('End time must be after start time');
        return;
      }
    } else {
      // Regular booking validation
      if (!selectedDate || !startTime || !endTime) {
        setError('Please select a date, start time, and end time');
        return;
      }

      if (parseInt(startTime) >= parseInt(endTime)) {
        setError('End time must be after start time');
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    setConflictDetails(null);

    try {
      // Create datetime strings - backend will interpret in EST/EDT timezone
      // Format: YYYY-MM-DDTHH:MM:SS (no timezone, Django will use TIME_ZONE setting)
      const startHour = parseInt(startTime);
      let endHour = parseInt(endTime);
      
      const startDate = selectedDate;
      const endDate = supportsMultiDay ? selectedEndDate : selectedDate;
      let endMinute = 0;
      
      // Treat the midnight option as the final minute of the selected day.
      if (endHour === 24) {
        endHour = 23;
        endMinute = 59;
      }
      
      const startDatetime = `${startDate}T${String(startHour).padStart(2, '0')}:00:00`;
      const endDatetime = `${endDate}T${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}:00`;

      const durationHours = endHour + endMinute / 60 - startHour;
      const requiresApproval = currentUser.role !== 'admin'
        && (isCampBooking || durationHours >= 8 || new Set(roomIds).size > 2);
      if (requiresApproval && !window.confirm('Bu bookingi Abdurrahman abi ile dogruladiniz mi')) {
        return;
      }

      const response = await createBooking({
        room_ids: roomIds,
        start_datetime: startDatetime,
        end_datetime: endDatetime,
        booking_type: isCampBooking ? 'camp' : 'regular',
      });

      // Success - navigate to success page or dashboard
      alert(response.data.status === 'Pending'
        ? "Your booking hasn't been confirmed yet. It is pending admin approval."
        : 'Booking confirmed successfully!');
      navigate('/bookingpage');
    } catch (err) {
      if (err.response?.status === 409) {
        // Conflict error
        setConflictDetails(err.response.data);
        setError(err.response.data.detail || 'Booking conflict detected');
      } else {
        setError(err.response?.data?.detail || 'Failed to create booking');
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Generate time options from available hours
  const generateTimeOptions = () => {
    if (availableHours.length === 0) return [];
    const allHours = availableHours;
    const options = [];
    
    // Add start time options
    for (let i = 0; i < allHours.length; i++) {
      options.push({
        value: allHours[i],
        label: formatHour(allHours[i]),
        type: 'start'
      });
    }
    
    return options;
  };

  const timeOptions = generateTimeOptions();
  
  // Generate end time options - only until next booking or midnight
  const getEndTimeOptions = () => {
    if (!startTime) return [];
    
    const startHour = parseInt(startTime);
    const options = [];
    
    // Find the earliest next booking after start time across all selected rooms
    let maxEndHour = 24; // Default to the final minute of the selected day.
    
    // Check unavailable slots to find the next booking that starts after our start time
    // We need to check all rooms in roomIds to find the earliest conflict
    for (const slot of unavailableSlots) {
      // Only check slots for rooms we're trying to book
      if (roomIds.includes(slot.room_id)) {
        const slotStartHour = slot.start_hour;
        
        // If this booking starts after our start time, limit end time to this booking's start
        if (slotStartHour !== undefined && slotStartHour > startHour && slotStartHour < maxEndHour) {
          maxEndHour = slotStartHour;
        }
      }
    }
    
    // Generate options from startHour + 1 to maxEndHour (inclusive)
    // Value 24 represents 11:59 PM on the selected day.
    const actualMaxHour = Math.min(maxEndHour, 24);
    
    for (let hour = startHour + 1; hour <= actualMaxHour; hour++) {
      options.push({
        value: hour,
        label: formatHour(hour)
      });
    }
    
    return options;
  };

  const endTimeOptions = getEndTimeOptions();
  const summaryDate = selectedDate
    ? new Date(`${selectedDate}T12:00:00`).toLocaleDateString('en-US', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : '';

  return (
    <div className="booking-form-container">
      <header className="page-intro"><span className="eyebrow">ONE STEP CLOSER</span><h2>Make it a date.</h2><p>Choose your schedule and confirm your room reservation.</p></header>
      
      {roomIds.length === 0 && (
        <div className="error-message">
          No rooms selected. Please go back and select rooms first.
          <button onClick={() => navigate('/bookingpage')} className="btn-secondary">
            Select Rooms
          </button>
        </div>
      )}

      {roomIds.length > 0 && (
        <form onSubmit={handleSubmit} className="booking-form">
          {isCampBooking && (
            <div className="camp-booking-notice" style={{ 
              backgroundColor: '#e8edf5', 
              padding: '15px', 
              borderRadius: '5px', 
              marginBottom: '20px',
              border: '2px solid #1a3970'
            }}>
              <strong>Camp Booking:</strong> {isAdmin ? 'Your booking will be approved automatically.' : 'This booking will require admin approval.'} Choose your start and end dates.
            </div>
          )}
          
          {/* Date Selection */}
          <div className="form-group">
            <label htmlFor="date">{supportsMultiDay ? 'Start Date *' : 'Select Date *'}</label>
            <input
              type="date"
              id="date"
              value={selectedDate}
              onChange={handleDateChange}
              min={isAdmin ? undefined : today}
              required
              className="form-input date-input"
            />
          </div>

          {/* End Date Selection for Camp Bookings */}
          {supportsMultiDay && (
            <div className="form-group">
              <label htmlFor="endDate">End Date *</label>
              <input
                type="date"
                id="endDate"
                value={selectedEndDate}
                onChange={handleEndDateChange}
                min={selectedDate || today}
                required
                className="form-input date-input"
              />
            </div>
          )}

          {/* Loading State - only show for regular bookings */}
          {!isCampBooking && loading && selectedDate && (
            <div className="loading-message">Loading availability...</div>
          )}


          {/* No Availability Message - only for regular bookings */}
          {!isCampBooking && !loading && selectedDate && availableHours.length === 0 && unavailableSlots.length > 0 && (
            <div className="no-availability">
              <p>No available hours for the selected date. Please choose another date.</p>
            </div>
          )}

          {/* Start Time Selection */}
          {selectedDate && (isCampBooking || availableHours.length > 0) && (
            <div className="form-group">
              <label htmlFor="startTime">Start Time *</label>
              {supportsMultiDay ? (
                <select
                  id="startTime"
                  value={startTime}
                  onChange={handleStartTimeChange}
                  required
                  className="form-input"
                >
                  <option value="">Select start time</option>
                  {Array.from({ length: 24 }, (_, i) => i).map(hour => (
                    <option key={hour} value={hour}>
                      {formatHour(hour)}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  id="startTime"
                  value={startTime}
                  onChange={handleStartTimeChange}
                  required
                  className="form-input"
                >
                  <option value="">Select start time</option>
                  {timeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {/* End Time Selection */}
          {startTime && (isCampBooking || true) && (
            <div className="form-group">
              <label htmlFor="endTime">End Time *</label>
              {supportsMultiDay ? (
                <select
                  id="endTime"
                  value={endTime}
                  onChange={handleEndTimeChange}
                  required
                  className="form-input"
                >
                  <option value="">Select end time</option>
                  {Array.from({ length: 25 }, (_, i) => i).map(hour => (
                    <option key={hour} value={hour}>
                      {formatHour(hour)}
                    </option>
                  ))}
                </select>
              ) : (
                <select
                  id="endTime"
                  value={endTime}
                  onChange={handleEndTimeChange}
                  required
                  className="form-input"
                >
                  <option value="">Select end time</option>
                  {endTimeOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          )}

          {selectedDate && startTime && endTime && (
            <section className="booking-review-summary" aria-label="Booking summary">
              <div className="booking-review-heading">
                <h3>{isTimeFirstBooking ? 'Confirm Your Selection' : 'Booking Summary'}</h3>
                {isTimeFirstBooking && <span>Time-first booking</span>}
              </div>
              <dl>
                <div>
                  <dt>Date</dt>
                  <dd>
                    {summaryDate}
                    {isCampBooking && selectedEndDate && selectedEndDate !== selectedDate
                      ? ` through ${new Date(`${selectedEndDate}T12:00:00`).toLocaleDateString('en-US')}`
                      : ''}
                  </dd>
                </div>
                <div>
                  <dt>Time</dt>
                  <dd>{formatHour(parseInt(startTime))} to {formatHour(parseInt(endTime))}</dd>
                </div>
                <div>
                  <dt>Rooms</dt>
                  <dd>
                    {isCampBooking
                      ? 'Downstairs'
                      : selectedRoomDetails.length > 0
                        ? selectedRoomDetails.map((room) => room.name).join(', ')
                        : 'Loading selected rooms...'}
                  </dd>
                </div>
              </dl>
            </section>
          )}

          {/* Error Display */}
          {error && (
            <div className="error-message">
              <strong>Error:</strong> {error}
            </div>
          )}

          {/* Conflict Details */}
          {conflictDetails && conflictDetails.conflicts && (
            <div className="conflict-details">
              <h4>Booking Conflicts Detected:</h4>
              <ul>
                {conflictDetails.conflicts.map((conflict, idx) => (
                  <li key={idx}>
                    <strong>{conflict.room}</strong> is already booked from{' '}
                    {new Date(conflict.start).toLocaleString()} to{' '}
                    {new Date(conflict.end).toLocaleString()}
                  </li>
                ))}
              </ul>
              <p>Please select a different time slot.</p>
            </div>
          )}

          {/* Submit Button */}
          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate('/bookingpage')}
              className="btn-secondary"
            >
              Back
            </button>
            <button
              type="submit"
              disabled={submitting || !selectedDate || !startTime || !endTime || (supportsMultiDay && !selectedEndDate)}
              className="btn-primary"
            >
              {submitting ? 'Creating Booking...' : isCampBooking ? 'Submit Camp Booking' : 'Complete Booking'}
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default BookingForm;

